import { useState } from 'react'

export const BASIC_AUTH_TOKEN_KEY = 'lens_basic_auth_token'

export function getBasicAuthToken(): string | null {
  return localStorage.getItem(BASIC_AUTH_TOKEN_KEY)
}

export function clearBasicAuthToken(): void {
  localStorage.removeItem(BASIC_AUTH_TOKEN_KEY)
}

interface BasicLoginScreenProps {
  onSuccess: () => void
}

export function BasicLoginScreen({ onSuccess }: BasicLoginScreenProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await fetch('/api/v1/auth/basic/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })
      if (res.status === 401) {
        setError('Incorrect username or password.')
        setLoading(false)
        return
      }
      if (res.status === 429) {
        setError('Too many attempts. Please wait and try again.')
        setLoading(false)
        return
      }
      if (!res.ok) {
        setError('Something went wrong. Please try again.')
        setLoading(false)
        return
      }
      const data = await res.json()
      localStorage.setItem(BASIC_AUTH_TOKEN_KEY, data.access_token)
      onSuccess()
    } catch {
      setError('Unable to reach the server. Please check your connection.')
      setLoading(false)
    }
  }

  return (
    <div className="basic-login-screen">
      <style>{`
        .basic-login-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          width: 100vw;
          position: fixed;
          top: 0;
          left: 0;
          background: var(--color-bg-primary);
          z-index: 9999;
          padding: 24px;
          box-sizing: border-box;
        }

        .basic-login-card {
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border);
          border-radius: 12px;
          padding: 40px 36px;
          width: 100%;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
        }

        .basic-login-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 28px;
        }

        .basic-login-logo img {
          width: 40px;
          height: 40px;
        }

        .basic-login-logo-text {
          font-size: 18px;
          font-weight: 700;
          color: var(--color-text-primary);
          letter-spacing: -0.3px;
        }

        .basic-login-title {
          font-size: 22px;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 6px;
          text-align: center;
        }

        .basic-login-subtitle {
          font-size: 14px;
          color: var(--color-text-secondary);
          margin: 0 0 28px;
          text-align: center;
        }

        .basic-login-form {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .basic-login-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .basic-login-field label {
          font-size: 13px;
          font-weight: 500;
          color: var(--color-text-primary);
        }

        .basic-login-field input {
          width: 100%;
          padding: 10px 12px;
          font-size: 14px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          background: var(--color-bg-tertiary);
          color: var(--color-text-primary);
          outline: none;
          transition: border-color 0.15s;
          box-sizing: border-box;
        }

        .basic-login-field input:focus {
          border-color: var(--color-accent);
          background: var(--color-bg-secondary);
          box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.12);
        }

        .basic-login-error {
          background: var(--color-danger-bg);
          border: 1px solid var(--color-danger);
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 13px;
          color: var(--color-danger);
          text-align: center;
        }

        .basic-login-submit {
          width: 100%;
          padding: 11px 14px;
          font-size: 14px;
          font-weight: 500;
          background: var(--color-accent);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.15s;
          margin-top: 4px;
        }

        .basic-login-submit:hover:not(:disabled) {
          background: var(--color-accent-hover);
        }

        .basic-login-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .basic-login-footer {
          margin-top: 24px;
          font-size: 12px;
          color: var(--color-text-tertiary);
          text-align: center;
        }
      `}</style>

      <div className="basic-login-card">
        <div className="basic-login-logo">
          <img src="/jamf-lens-icon.svg" alt="Jamf Lens" />
          <span className="basic-login-logo-text">Jamf Lens</span>
        </div>

        <h1 className="basic-login-title">Sign in</h1>
        <p className="basic-login-subtitle">Enter your credentials to continue</p>

        <form className="basic-login-form" onSubmit={submit}>
          <div className="basic-login-field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
              disabled={loading}
            />
          </div>

          <div className="basic-login-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="basic-login-error" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="basic-login-submit"
            disabled={loading || !username || !password}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>

      <p className="basic-login-footer">Jamf Lens · Pilot Access</p>
    </div>
  )
}

export default BasicLoginScreen
